import React from "react";
import { useTranslation } from "react-i18next";
import { useHotel } from "@/hooks/useHotel";
import { toast } from "sonner";
import { PageState } from "@/components/common/PageState";
import { SyncEventStatsCard } from "./components/SyncEventStatsCard";
import { RetryStatsCard } from "./components/RetryStatsCard";
import { ReconciliationCard } from "./components/ReconciliationCard";
import {
  useGetSyncStats,
  useGetRetryStats,
  useGetReconciliationStatus,
  useTriggerReconciliation,
} from "./hooks/pms.hooks";

export const SyncStatsPage = () => {
  const { hotelId } = useHotel();
  const { t } = useTranslation();

  const {
    data: syncStats,
    isLoading: syncLoading,
    isError: syncError,
  } = useGetSyncStats();
  const {
    data: retryStats,
    isLoading: retryLoading,
    isError: retryError,
  } = useGetRetryStats();
  const {
    data: reconciliation,
    isLoading: reconLoading,
    isError: reconError,
  } = useGetReconciliationStatus();
  const triggerReconciliation = useTriggerReconciliation();

  if (!hotelId) {
    return <PageState text={t("pms.hotelNotSelected")} />;
  }

  if (syncLoading || retryLoading || reconLoading) {
    return <PageState text={t("pms.loading")} />;
  }

  if (syncError || retryError || reconError) {
    return <PageState text={t("pms.stats.loadError")} tone="error" />;
  }

  if (!syncStats && !retryStats && !reconciliation) {
    return <PageState text={t("pms.stats.empty")} />;
  }

  const handleTriggerReconciliation = () => {
    triggerReconciliation.mutate(undefined, {
      onSuccess: () =>
        toast.success(t("pms.stats.reconciliation.triggerSuccess")),
      onError: () => toast.error(t("pms.stats.reconciliation.triggerError")),
    });
  };

  return (
    <div className="flex flex-col gap-6 p-12">
      {syncStats && <SyncEventStatsCard stats={syncStats} />}
      {retryStats && <RetryStatsCard stats={retryStats} />}
      {reconciliation && (
        <ReconciliationCard
          status={reconciliation}
          isTriggering={triggerReconciliation.isPending}
          onTrigger={handleTriggerReconciliation}
        />
      )}
    </div>
  );
};
